/**
 * Service settings, as the Service screens read them. 15-10-2026, migration 0212.
 *
 * The Service module's own rules live in service_settings, its own table (the
 * Materials side reads company_profile through src/lib/settings.ts - the two
 * modules share neither). The System Administrator changes them on
 * Service Procurement -> Settings.
 *
 * Every getter falls back to the number that was written into the code before
 * the setting existed, so a device that cannot read the table, or a database
 * without the row yet, behaves exactly as it always did.
 */
import { supabase } from '@/lib/supabase'
import { useQuery } from '@/lib/data'

/** Today's numbers. Also the starting values of the rows in 0212. */
export const SERVICE_SETTING_DEFAULTS = {
  woRetentionDefaultPct: 10,          // wo_retention_default_pct
  woDlpDefaultMonths: 12,             // wo_dlp_default_months
  woAdvanceRecoveryDefaultPct: 20,    // wo_advance_recovery_default_pct
  contractorOverdueDays: 45,          // contractor_overdue_days
} as const

const num = (v: unknown, fallback: number) => {
  const n = Number(v)
  return v === null || v === undefined || v === '' || !Number.isFinite(n) ? fallback : n
}

export function useServiceSettings() {
  const { data, loading } = useQuery<{ key: string; value: string }>('service_settings_all',
    () => supabase.from('service_settings').select('key, value'), [], { idField: 'key' })
  const v: Record<string, string> = Object.fromEntries(data.map((r) => [r.key, r.value]))
  return {
    loading,
    /** The table has actually been read (from the server or the cache). */
    ready: data.length > 0,
    woRetentionDefaultPct: num(v.wo_retention_default_pct, SERVICE_SETTING_DEFAULTS.woRetentionDefaultPct),
    woDlpDefaultMonths: num(v.wo_dlp_default_months, SERVICE_SETTING_DEFAULTS.woDlpDefaultMonths),
    woAdvanceRecoveryDefaultPct: num(v.wo_advance_recovery_default_pct, SERVICE_SETTING_DEFAULTS.woAdvanceRecoveryDefaultPct),
    contractorOverdueDays: num(v.contractor_overdue_days, SERVICE_SETTING_DEFAULTS.contractorOverdueDays),
  }
}
